/**
 * AffiliateTiersPage — ladder of volume-based payout tier multipliers.
 *
 * Layout:
 *   - Brand header (GREEN accent, zinc borders).
 *   - Create-tier form: name, thresholds, multiplier, color, sort order.
 *   - Ladder list: one card per tier, sorted by sort_order ASC, showing
 *     multiplier pill, thresholds, color swatch, and a delete action.
 *
 * All network access goes through ./api (D6). Colors: brand green; no
 * forbidden (emerald/sky/indigo/teal) chrome.
 */
import { useState, type FormEvent } from "react";
import {
	useAffiliateTiersList,
	useAffiliateTiersCreate,
	useAffiliateTiersDelete,
} from "./hooks";
import {
	formatMultiplier,
	formatRevenueCents,
	multiplierToBps,
	sortTiersForDisplay,
	type AffiliateTierView,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const PILL_MULTIPLIER =
	"inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-800 ring-1 ring-inset ring-green-200";
const LABEL =
	"flex flex-col gap-1 text-xs font-medium text-zinc-700";

const DEFAULT_COLOR = "#16a34a"; // brand green-600

function isValidHex(v: string): boolean {
	return /^#[0-9a-fA-F]{6}$/.test(v);
}

export function AffiliateTiersPage() {
	const listQuery = useAffiliateTiersList();
	const createMut = useAffiliateTiersCreate();
	const deleteMut = useAffiliateTiersDelete();

	const [name, setName] = useState("");
	const [minConversions, setMinConversions] = useState("0");
	const [minRevenueDollars, setMinRevenueDollars] = useState("0");
	const [multiplier, setMultiplier] = useState("1.0");
	const [color, setColor] = useState(DEFAULT_COLOR);
	const [sortOrder, setSortOrder] = useState("0");
	const [formError, setFormError] = useState<string | null>(null);

	const tiers = sortTiersForDisplay(listQuery.data?.tiers ?? []);

	const resetForm = () => {
		setName("");
		setMinConversions("0");
		setMinRevenueDollars("0");
		setMultiplier("1.0");
		setColor(DEFAULT_COLOR);
		setSortOrder("0");
		setFormError(null);
	};

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		setFormError(null);

		const trimmedName = name.trim();
		if (trimmedName.length === 0) {
			setFormError("Name is required.");
			return;
		}

		const convNum = Number(minConversions);
		if (!Number.isInteger(convNum) || convNum < 0) {
			setFormError("Min conversions must be a non-negative integer.");
			return;
		}

		const revNum = Number(minRevenueDollars);
		if (!Number.isFinite(revNum) || revNum < 0) {
			setFormError("Min revenue must be non-negative.");
			return;
		}
		const minRevenueCents = Math.round(revNum * 100);

		const mult = Number(multiplier);
		if (!Number.isFinite(mult) || mult < 1.0 || mult > 2.0) {
			setFormError("Multiplier must be between 1.0 and 2.0.");
			return;
		}
		const payoutMultiplierBps = multiplierToBps(mult);

		if (!isValidHex(color)) {
			setFormError("Color must be a hex value like #16a34a.");
			return;
		}

		const sortNum = Number(sortOrder);
		if (!Number.isInteger(sortNum)) {
			setFormError("Sort order must be an integer.");
			return;
		}

		createMut.mutate(
			{
				name: trimmedName,
				minConversions: convNum,
				minRevenueCents,
				payoutMultiplierBps,
				color: color.toLowerCase(),
				sortOrder: sortNum,
			},
			{
				onSuccess: () => {
					resetForm();
				},
			},
		);
	};

	return (
		<main
			data-testid="affiliate-tiers-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					affiliate-tiers
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Affiliate Tiers
				</h1>
				<p className="text-sm text-zinc-600">
					Volume-based payout multipliers. Each tier defines 30-day thresholds
					(conversions and revenue) and a payout multiplier applied when an
					affiliate qualifies.
				</p>
			</header>

			<section className={CARD} aria-label="Create tier">
				<h2 className="text-base font-semibold text-zinc-900">
					Create a new tier
				</h2>
				<form
					onSubmit={handleCreate}
					className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
					data-testid="affiliate-tiers-create-form"
				>
					<label className={LABEL}>
						<span>Name</span>
						<input
							type="text"
							className={INPUT}
							placeholder="Gold"
							value={name}
							onChange={(e) => setName(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="affiliate-tiers-name-input"
							maxLength={64}
						/>
					</label>

					<label className={LABEL}>
						<span>Min conversions (30d)</span>
						<input
							type="number"
							inputMode="numeric"
							className={INPUT}
							value={minConversions}
							min={0}
							step={1}
							onChange={(e) => setMinConversions(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="affiliate-tiers-min-conversions-input"
						/>
					</label>

					<label className={LABEL}>
						<span>Min revenue (USD, 30d)</span>
						<input
							type="number"
							inputMode="decimal"
							className={INPUT}
							value={minRevenueDollars}
							min={0}
							step="0.01"
							onChange={(e) => setMinRevenueDollars(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="affiliate-tiers-min-revenue-input"
						/>
					</label>

					<label className={LABEL}>
						<span>Payout multiplier (1.0..2.0)</span>
						<input
							type="number"
							inputMode="decimal"
							className={INPUT}
							value={multiplier}
							min={1.0}
							max={2.0}
							step={0.01}
							onChange={(e) => setMultiplier(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="affiliate-tiers-multiplier-input"
						/>
					</label>

					<label className={LABEL}>
						<span>Swatch color (hex)</span>
						<div className="flex items-center gap-2">
							<span
								aria-hidden="true"
								className="inline-block h-6 w-6 shrink-0 rounded-[4px] border border-zinc-300"
								style={{ backgroundColor: isValidHex(color) ? color : "#d4d4d8" }}
								data-testid="affiliate-tiers-color-preview"
							/>
							<input
								type="text"
								className={INPUT}
								value={color}
								onChange={(e) => setColor(e.currentTarget.value)}
								disabled={createMut.isPending}
								placeholder="#16a34a"
								data-testid="affiliate-tiers-color-input"
							/>
						</div>
					</label>

					<label className={LABEL}>
						<span>Sort order</span>
						<input
							type="number"
							inputMode="numeric"
							className={INPUT}
							value={sortOrder}
							step={1}
							onChange={(e) => setSortOrder(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="affiliate-tiers-sort-order-input"
						/>
					</label>

					<div className="sm:col-span-2">
						<button
							type="submit"
							className={BTN}
							disabled={createMut.isPending || !name.trim()}
							data-testid="affiliate-tiers-submit"
						>
							{createMut.isPending ? "Saving." : "Create tier"}
						</button>
					</div>
				</form>
				{formError ? (
					<p
						className="mt-3 text-xs text-red-600"
						role="alert"
						data-testid="affiliate-tiers-form-error"
					>
						{formError}
					</p>
				) : null}
				{createMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="affiliate-tiers-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Tier ladder">
				<h2 className="text-base font-semibold text-zinc-900">Tier ladder</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="affiliate-tiers-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="affiliate-tiers-list-error"
					>
						{listQuery.error.message}
					</p>
				) : tiers.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="affiliate-tiers-empty"
					>
						No tiers defined yet. Add the first tier above to start the ladder.
					</p>
				) : (
					<ul
						className="mt-3 flex flex-col gap-2"
						data-testid="affiliate-tiers-list"
					>
						{tiers.map((tier) => (
							<TierRow
								key={tier.id}
								tier={tier}
								onDelete={(id) => deleteMut.mutate(id)}
								isDeleting={
									deleteMut.isPending && deleteMut.variables === tier.id
								}
							/>
						))}
					</ul>
				)}
				{deleteMut.error ? (
					<p
						className="mt-3 text-xs text-red-600"
						role="alert"
						data-testid="affiliate-tiers-delete-error"
					>
						{deleteMut.error.message}
					</p>
				) : null}
			</section>
		</main>
	);
}

function TierRow({
	tier,
	onDelete,
	isDeleting,
}: {
	tier: AffiliateTierView;
	onDelete: (id: string) => void;
	isDeleting: boolean;
}) {
	return (
		<li
			className="flex items-center justify-between gap-4 rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm"
			data-testid={`affiliate-tiers-row-${tier.id}`}
		>
			<div className="flex min-w-0 items-center gap-3">
				<span
					aria-hidden="true"
					className="inline-block h-4 w-4 shrink-0 rounded-full border border-zinc-300"
					style={{ backgroundColor: tier.color }}
				/>
				<div className="flex min-w-0 flex-col">
					<div className="flex flex-wrap items-center gap-2">
						<span className="font-medium text-zinc-900">{tier.name}</span>
						<span
							className={PILL_MULTIPLIER}
							data-testid={`affiliate-tiers-multiplier-${tier.id}`}
						>
							{formatMultiplier(tier.payout_multiplier_bps)}
						</span>
					</div>
					<p className="mt-0.5 text-xs text-zinc-600">
						<span data-testid={`affiliate-tiers-min-conversions-${tier.id}`}>
							{tier.min_conversions}
						</span>
						{" conv / "}
						<span data-testid={`affiliate-tiers-min-revenue-${tier.id}`}>
							{formatRevenueCents(tier.min_revenue_cents)}
						</span>
						{" rev (30d)"}
						{" \u00B7 sort "}
						{tier.sort_order}
					</p>
				</div>
			</div>
			<button
				type="button"
				className={BTN_GHOST}
				onClick={() => onDelete(tier.id)}
				disabled={isDeleting}
				data-testid={`affiliate-tiers-delete-${tier.id}`}
			>
				{isDeleting ? "Deleting." : "Delete"}
			</button>
		</li>
	);
}

export default AffiliateTiersPage;
